// import React, { useState } from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Button,
  ButtonProps,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { Email, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  PasswordStrengthCheck,
  ValidateEmailIsOrg,
  ValidateIsEmail,
} from "../../../Shared-ui/utils/Auth/AuthHelpers";
import Strength from "../../../Shared-ui/Types/Auth";
import variables from "../../../assets/styles/variables.module.scss";
import AuthCommon from "../../Components/Auth";
import { isOnlyAlphabets } from "../../../Shared-ui/utils/Common/Common";
import axios from "axios";
import {
  OTP_VERIFY,
  RE_SEND_OTP,
  REGISTER_INDIVIDUAL,
  SEND_OTP,
} from "../../../Services/Endpoint";
import { json } from "stream/consumers";
import { useDispatch } from "react-redux";
import {
  StartLoading,
  StopLoading,
} from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import {
  ShowError,
  ShowSuccess,
} from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import { useNavigate } from "react-router-dom";
import { Register } from "../../../Shared-ui/Types/User";
import { v4 as uuidv4 } from "uuid";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
//import { styled } from "@mui/material/styles";

const VerificationBox = styled(Box)({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#80bdff",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1976d2",
    },
  },
  "& input": {
    textAlign: "center",
    letterSpacing: "0.5em",
    fontSize: "1.5rem",
  },
});

const SignUp = () => {
  const errorInitial = {
    email: { error: "", value: false },
    name: { error: "", value: false },
    organization_id: { error: "", value: false },
    password: { error: "", value: false },
  };
  const registerInitial = {
    email: "",
    full_name: "",
    password: "",
    subscription: "",
  };
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [strength, setStrength] = useState<Strength>({
    label: "Password strength ",
    color: "",
    strength: "",
  });
  const [registerForm, setRegisterForm] = useState<Register>({
    ...registerInitial,
  });
  const [registerFormEr, setRegisterFormEr] = useState<any>(errorInitial);
  const [showPassword, setShowPassword] = useState<boolean>(false); // New state for toggle
  const [verificationCode, setVerificationCode] = useState<string>("");

  // Array for dropdown menu items
  const subscriptionOptions = [
    { value: 1, label: "Basic", color: variables.BasicColor },
    { value: 2, label: "Starter", color: variables.starterColor },
    { value: 3, label: "Professional", color: variables.professionalColor },
    { value: 4, label: "Enterprise", color: variables.enterpriseColor },
  ];

  const [isOtpSent, setIsOtpSent] = useState<boolean>(false);
  const [isOtpVerify, setIsOtpVerify] = useState<boolean>(false);
  const [isOtpVerifyMsg, setIsOtpVerifyMsg] = useState<boolean>(false);
  const [otpValues, setOtpValues] = useState<string[]>(Array(6).fill(""));
  const [otpSentEmail, setOtpSentEmail] = useState<string>("");

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;

    if (name === "password") {
      setStrength(
        value
          ? PasswordStrengthCheck(value)
          : { label: "Password strength ", color: "", strength: "" }
      );
      if (value.length > 0 && Number(PasswordStrengthCheck(value).strength) >= 80) {
        setRegisterFormEr((preVal: any) => ({
          ...preVal,
          password: { error: "", value: false }, // Clear password error
        }));
      }
    }
    setRegisterForm((preVal: any) => ({ ...preVal, [name]: value }));
  };

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev); // Toggle show/hide
  };

  const handleSubscriptionChange = (event: SelectChangeEvent<number>) => {
    let value = event.target.value as number;
    setRegisterForm((preVal: any) => ({
      ...preVal,
      subscription: event.target.value,
    }));
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^[0-9]*$/.test(value)) return;

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);
    setVerificationCode(newOtpValues.join(""));

    // Auto focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>
  ) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      const prevInput = document.getElementById(
        `otp-${index - 1}`
      ) as HTMLInputElement;
      prevInput?.focus();
    }
  };

  const validateRegisterForm = () => {
    let isValid = true;
    const newErrors = { ...errorInitial };
    // email valiadtion
    if (!ValidateIsEmail(registerForm.email)) {
      setRegisterFormEr((preVal: any) => ({
        ...preVal,
        email: { error: "Please enter valid email", value: true },
      }));
      return false;
    } else if (
      !ValidateEmailIsOrg(registerForm.email) ||
      !registerForm.email.trim()
    ) {
      setRegisterFormEr((preVal: any) => ({
        ...preVal,
        email: { error: "Personal emails are not allowed", value: true },
      }));
      return false;
    } else {
      setRegisterFormEr((preVal: any) => ({
        ...preVal,
        email: { error: "", value: false },
      }));
    }

    // name validation
    if (!registerForm.full_name?.trim()) {
      setRegisterFormEr((preVal: any) => ({
        ...preVal,
        name: { error: "Please enter name", value: true },
      }));
      return false;
    } else if (!isOnlyAlphabets(registerForm.full_name)) {
      setRegisterFormEr((preVal: any) => ({
        ...preVal,
        name: {
          error: "Numbers and special character are not allowed",
          value: true,
        },
      }));
      return false;
    } else {
      setRegisterFormEr((preVal: any) => ({
        ...preVal,
        name: { error: "", value: false },
      }));
    }

    // org validation
    // if (registerForm.organization_id.length < 2) {
    //     setRegisterFormEr((preVal: any) => ({ ...preVal, organization_id: { error: "Please enter minum charater 2", value: true } }))
    //     return false
    // } else if (!isOnlyAlphabets(registerForm.organization_id)) {
    //     setRegisterFormEr((preVal: any) => ({ ...preVal, organization_id: { error: "Numbers and special character are not allowed", value: true } }))
    //     return false
    // } else {
    //     setRegisterFormEr((preVal: any) => ({ ...preVal, organization_id: { error: "", value: false } }))
    // }

    const passwordValue = registerForm.password;
    const numericStrength = Number(strength.strength);

    if (!passwordValue.trim()) {
      setRegisterFormEr((preVal: any) => ({
        ...preVal,
        password: { error: "Password cannot be empty.", value: true },
      }));
      return false;
    } else if (numericStrength < 80) {
      setRegisterFormEr((preVal: any) => ({
        ...preVal,
        password: { error: "Password is too weak.", value: true },
      }));
      return false;
    } else {
      setRegisterFormEr((preVal: any) => ({
        ...preVal,
        password: { error: "", value: false },
      }));
    }
    return true;
  };
  const handleSendOTP = async () => {
    if (!ValidateIsEmail(registerForm.email)) {
      setRegisterFormEr((preVal: any) => ({
        ...preVal,
        email: { error: "Please enter valid email", value: true },
      }));
      return
    } else {
      setRegisterFormEr((preVal: any) => ({
        ...preVal,
        email: { error: "", value: false },
      }));
    }
    try {
      dispatch(StartLoading());
      const response: any = await axios.post(SEND_OTP, {
        email: registerForm.email,
      });
      setIsOtpSent(true);
      setOtpSentEmail(registerForm.email);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
      console.log(err);
    } finally {
      dispatch(StopLoading());
    }
    setCountdown(300);
    setCanResend(false);
  };

  const formatCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleReSendOTP = async () => {
    try {
      dispatch(StartLoading());
      const response: any = await axios.post(RE_SEND_OTP, {
        email: otpSentEmail,
      });
      setIsOtpSent(true);
      setOtpSentEmail(registerForm.email);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
      console.log(err);
    } finally {
      dispatch(StopLoading());
    }
    setCountdown(300);
    setCanResend(false);
  };

  const handleVerifyOTP = async () => {
    try {
      if (!ValidateIsEmail(registerForm.email)) {
        setRegisterFormEr((preVal: any) => ({
          ...preVal,
          email: { error: "Please enter valid email", value: true },
        }));
        return
      } else {
        setRegisterFormEr((preVal: any) => ({
          ...preVal,
          email: { error: "", value: false },
        }));
      }
      dispatch(StartLoading());
      const response: any = await axios.post(OTP_VERIFY, {
        email: registerForm.email,
        otp_code: verificationCode,
      });
      if (!response.data.verified) {
        dispatch(ShowError(response.data?.message));
      } else {
        setIsOtpVerify(response.data?.verified || false)
        setIsOtpVerifyMsg(response.data?.message || "")
      }
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
      console.log(err);
    } finally {
      dispatch(StopLoading());
    }
  };

  const handleSubmit = async () => {
    if (!validateRegisterForm()) return;
    try {
      dispatch(StartLoading());
      const response: any = await axios.post(REGISTER_INDIVIDUAL, registerForm);
      dispatch(ShowSuccess("User registration done"));
      setIsOtpSent(false);
      navigate("/login");
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
      console.log(err);
    } finally {
      dispatch(StopLoading());
    }
  };

  const ColorButton = styled(Button)<ButtonProps>(({ theme }) => ({
    color: variables.whiteColor,
    backgroundColor: variables.primaryColor,
    "&:hover": {
      backgroundColor: variables.secondaryColor,
    },
  }));

  const handleVerificationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setVerificationCode(value);
  };

  const [countdown, setCountdown] = useState(30); // 30 seconds countdown
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isOtpSent && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }

    if (countdown === 0) {
      setCanResend(true);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isOtpSent, countdown]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          minHeight: "calc(100vh - 78px)",
          flexDirection: { md: "row", sm: "column", xs: "column" },
        }}
      >
        <AuthCommon />
        <Box
          sx={{
            display: "flex",
            width: "100%",
            px: { xl: 30, lg: 20, md: 8, sm: 8, xs: 5 },
            py: { sm: 6, xs: 3 },
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Typography variant="h4" fontWeight="500" gutterBottom>
              Sign up
            </Typography>
            <Typography variant="body2" fontWeight="500" gutterBottom>
              Enter email to send one time Password
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <FormControl
                fullWidth
                variant="outlined"
                error={registerFormEr.email.value}
              >
                <InputLabel
                  htmlFor="email"
                  shrink
                  sx={{
                    background: "#fff",
                    px: 0.5,
                  }}
                >
                  Business Email *
                </InputLabel>
                <TextField
                  name="email"
                  id="email"
                  placeholder="Enter your business email"
                  sx={{ background: "white" }}
                  error={registerFormEr.email.value}
                  helperText={registerFormEr.email.error}
                  onChange={(e) => handleChange(e)}
                  inputProps={{
                    maxLength: 50,
                  }}
                />
              </FormControl>
              {!isOtpSent && (
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSendOTP}
                  disableElevation
                  disabled={!registerForm.email}
                  sx={{
                    backgroundColor: variables.primaryColor,
                    color: variables.whiteColor,
                    textTransform: "none",
                    minWidth: "120px",
                    mt: 1,
                    "&:hover": {
                      backgroundColor: variables.secondaryColor,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: variables.softGray,
                      color: "text.secondary",
                    },
                  }}
                >
                  Send OTP
                </Button>
              )}
              {/* {registerFormEr.email.error && (
                <FormHelperText error>
                  {registerFormEr.email.error}
                </FormHelperText>
              )} */}
            </Box>

            {isOtpSent && !isOtpVerify && (
              <Box >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    background: "#E0F6F0",
                    borderLeft: "4px solid #23B684",
                    borderRadius: "10px",
                    px: 2,
                    py: 1.5,
                    mb: 2,
                    color: "#23B684",
                    position: "relative",
                    minHeight: 56,
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 20, mr: 1 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#23B684",
                        fontWeight: 500,
                        lineHeight: 1.2,
                      }}
                    >
                      Verification code sent to
                      <br />
                      {otpSentEmail}
                    </Typography>
                  </Box>
                  {countdown > 0 && (
                    <Typography
                      component="span"
                      sx={{
                        color: "#23B684",
                        fontWeight: 500,
                        ml: 2,
                        minWidth: 48,
                        textAlign: "right",
                      }}
                    >
                      ({formatCountdown(countdown)})
                    </Typography>
                  )}
                </Box>

                <VerificationBox>
                  <FormControl fullWidth variant="outlined">
                    {/* <InputLabel shrink sx={{ background: "#fff", px: 0.5 }}>
                      Enter 6-digit code
                    </InputLabel> */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        mt: 1,
                        justifyContent: "space-between",
                      }}
                    >
                      {Array(6)
                        .fill(0)
                        .map((_, index) => (
                          <TextField
                            key={index}
                            id={`otp-${index}`}
                            value={otpValues[index]}
                            onChange={(e) =>
                              handleOtpChange(index, e.target.value)
                            }
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            inputProps={{
                              maxLength: 1,
                              inputMode: "numeric",
                              pattern: "[0-9]*",
                              style: {
                                textAlign: "center",
                                fontSize: "1.5rem",
                                padding: "8px",
                                width: "60px",
                                letterSpacing: "normal",
                              },
                            }}
                            sx={{
                              background: "white",
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "#e0e0e0",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#80bdff",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#1976d2",
                                },
                              },
                            }}
                          />
                        ))}
                    </Box>
                  </FormControl>
                </VerificationBox>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                    gap: 2,
                  }}
                >
                  <Button
                    fullWidth
                    size="large"
                    disableElevation
                    variant="contained"
                    onClick={() => handleVerifyOTP()}
                    disabled={verificationCode.length !== 6 || !registerForm.email}
                    sx={{
                      textTransform: "capitalize",
                      backgroundColor: variables.primaryColor,
                      color: variables.whiteColor,
                      "&:hover": {
                        backgroundColor: variables.secondaryColor,
                      },
                    }}
                  >
                    Verify
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={() => handleReSendOTP()}
                    disabled={!canResend || !registerForm.email}
                    sx={{
                      color: variables.primaryButton,
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Resend
                  </Button>
                </Box>
              </Box>
            )}

            {isOtpVerify && (
              <>
                {/* <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                otp verification done
              </Typography> */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: "#E8F5E9",
                    color: "#2E7D32",
                    p: 2,
                    borderRadius: 1,
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
                  <Typography>{`${isOtpVerifyMsg}!`}</Typography>
                </Box>

                <FormControl
                  fullWidth
                  variant="outlined"
                  error={registerFormEr.name.value}
                >
                  <InputLabel
                    htmlFor="name"
                    shrink
                    sx={{
                      background: "#fff",
                      px: 0.5,
                    }}
                  >
                    Name *
                  </InputLabel>
                  <TextField
                    id="full_name"
                    name="full_name"
                    placeholder="Enter your name"
                    sx={{ background: "white" }}
                    error={registerFormEr.name.value}
                    helperText={registerFormEr.name.error}
                    onChange={(e) => handleChange(e)}
                    inputProps={{
                      maxLength: 50,
                    }}
                  />
                </FormControl>

                <Box>
                  <FormControl fullWidth variant="outlined" error={registerFormEr.password.value}>
                    <InputLabel
                      htmlFor="password"
                      shrink
                      sx={{ background: "#fff", px: 0.5 }}
                    >
                      Password *
                    </InputLabel>
                    <OutlinedInput
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={registerForm.password}
                      onChange={handleChange}
                      placeholder="Password"
                      sx={{ background: "white" }}
                      inputProps={{ maxLength: 32 }}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleTogglePassword}
                            edge="end"
                            aria-label="toggle password visibility"
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      }
                      label="Password"
                    />
                    {registerFormEr.password.value && (
                      <FormHelperText error>
                        {registerFormEr.password.error}
                      </FormHelperText>
                    )}
                  </FormControl>

                  {/* Password Strength Indicator */}
                  <Box sx={{ mt: 1 }}>
                    <Box
                      sx={{
                        height: "4px",
                        width: "100%",
                        backgroundColor: "lightgray",
                        borderRadius: "2px",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          width: `${strength.strength}%`,
                          backgroundColor: strength.color,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      color={strength.color}
                      sx={{ mt: 1, textAlign: "right", display: "block" }}
                    >
                      {strength.label}
                    </Typography>
                  </Box>
                </Box>
                <ColorButton
                  variant="contained"
                  disableElevation
                  size="large"
                  disabled={!isOtpVerify ? true : false}
                  sx={{
                    textTransform: "capitalize",
                    fontWeight: "400",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => handleSubmit()}
                >
                  Sign up
                </ColorButton>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SignUp;
